import { reactive, computed, provide, inject, watch } from 'vue';
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';
import type { RouterItem, RouterItemsState, RouterItemsOptions } from './types';

// LocalStorage keys
const LS_ITEMS = 'itp/router/items';
const LS_ACTIVE = 'itp/router/active';
const LS_HISTORY = 'itp/router/history';
const LS_SCROLL = 'itp/router/scroll';

// DI key
const ControllerKey = Symbol('ItpRouterItemsController');

// ---------- helpers ----------

function toRouteKey(route: RouteLocationNormalizedLoaded) {
  return route.fullPath || route.path;
}

function readMeta(route: RouteLocationNormalizedLoaded) {
  const meta = route.meta || {};
  return {
    title: (meta.title as string) || (route.name?.toString() ?? route.path),
    icon: (meta.icon as string) || undefined,
    tooltip: (meta.tooltip as string) || undefined,
    closable: meta.closable !== false,
  };
}

// keep affix items in front of normal items
function reorderWithAffixFirst(
  routerItems: RouterItem[],
  desiredIds: string[],
) {
  const affixItems = routerItems.filter((item) => item.closable === false);
  const normalItems = routerItems.filter((item) => item.closable !== false);

  const byId = new Map(normalItems.map((item) => [item.id, item]));
  const reorderedNormals = desiredIds
    .map((id) => byId.get(id)!)
    .filter(Boolean);

  return [...affixItems, ...reorderedNormals];
}

// ---------- controller factory ----------

export function createRouterItemsController(
  router: Router,
  options?: RouterItemsOptions,
) {
  const state = reactive<RouterItemsState>({
    items: [],
    activeId: null,
    history: [],
  });

  // load cache
  try {
    const cachedItems = JSON.parse(
      localStorage.getItem(LS_ITEMS) || '[]',
    ) as RouterItem[];

    const cachedActiveId = JSON.parse(
      localStorage.getItem(LS_ACTIVE) || 'null',
    ) as string | null;

    const cachedHistory = JSON.parse(
      localStorage.getItem(LS_HISTORY) || '[]',
    ) as string[];

    state.items = cachedItems;
    state.activeId = cachedActiveId;
    state.history = cachedHistory;
  } catch {
    console.error('Failed to load router items cache.');
  }

  function resolveHomeId(): string | null {
    if (!options?.affixHome) {
      return null;
    }

    const resolved = options.affixHome.name
      ? router.resolve({ name: options.affixHome.name })
      : router.resolve({ path: options.affixHome.path || '/' });

    return resolved.fullPath || resolved.path;
  }

  function ensureAffixHome() {
    if (!options?.affixHome) {
      return;
    }

    const homeId = resolveHomeId();
    if (!homeId) {
      return;
    }

    const resolvedHome = options.affixHome.name
      ? router.resolve({ name: options.affixHome.name })
      : router.resolve({ path: options.affixHome.path || '/' });

    const homeTitle =
      options.affixHome.title ||
      (resolvedHome.meta.title as string) ||
      (resolvedHome.name?.toString() ?? resolvedHome.path);

    const existingIndex = state.items.findIndex((item) => item.id === homeId);

    if (existingIndex >= 0) {
      const existingHome = state.items[existingIndex];

      existingHome.closable = false;
      existingHome.title = homeTitle || existingHome.title;
      existingHome.icon =
        options.affixHome.icon ??
        (resolvedHome.meta.icon as string | undefined) ??
        existingHome.icon;
      existingHome.tooltip =
        options.affixHome.tooltip ??
        (resolvedHome.meta.tooltip as string | undefined) ??
        existingHome.tooltip;
      existingHome.path = homeId;

      if (existingIndex !== 0) {
        state.items.splice(existingIndex, 1);
        state.items.unshift(existingHome);
      }
    } else {
      const newHomeItem: RouterItem = {
        id: homeId,
        path: homeId,
        title: homeTitle,
        icon:
          options.affixHome.icon ??
          (resolvedHome.meta.icon as string | undefined),
        tooltip:
          options.affixHome.tooltip ??
          (resolvedHome.meta.tooltip as string | undefined),
        closable: false,
        createdAt: 0,
      };

      state.items.unshift(newHomeItem);
      state.history = [homeId, ...state.history.filter((id) => id !== homeId)];
    }

    if (!state.activeId) {
      state.activeId = homeId;
    }
  }

  const activeItem = computed(
    () => state.items.find((item) => item.id === state.activeId) || null,
  );

  // sync model with router location
  function syncFromRoute(route: RouteLocationNormalizedLoaded) {
    const targetId = toRouteKey(route);
    const meta = readMeta(route);

    const existingItem = state.items.find((item) => item.id === targetId);

    if (!existingItem) {
      const newItem: RouterItem = {
        id: targetId,
        path: route.fullPath || route.path,
        title: meta.title,
        icon: meta.icon,
        tooltip: meta.tooltip,
        closable: meta.closable ?? true,
        createdAt: Date.now(),
      };
      state.items.push(newItem);
    } else {
      existingItem.title = meta.title || existingItem.title;
      existingItem.icon = meta.icon ?? existingItem.icon;
      existingItem.tooltip = meta.tooltip ?? existingItem.tooltip;

      if (existingItem.closable !== false) {
        existingItem.closable = meta.closable ?? true;
      }

      existingItem.path = route.fullPath || route.path;
    }

    state.activeId = targetId;
    state.history = [
      targetId,
      ...state.history.filter((id) => id !== targetId),
    ];

    const homeId = resolveHomeId();

    if (homeId && targetId === homeId) {
      const homeItem = state.items.find((item) => item.id === homeId);

      if (homeItem) {
        homeItem.closable = false;
      }
    }
  }

  // close by id
  function close(itemId: string) {
    const removeIndex = state.items.findIndex((item) => item.id === itemId);

    if (removeIndex < 0) {
      return;
    }

    if (state.items[removeIndex].closable === false) {
      return;
    }

    state.items.splice(removeIndex, 1);
    state.history = state.history.filter((id) => id !== itemId);

    if (state.activeId === itemId) {
      const mostRecentExisting =
        state.history.find((id) =>
          state.items.some((item) => item.id === id),
        ) || null;
      state.activeId = mostRecentExisting;

      const homeId = resolveHomeId();

      if (
        !state.activeId &&
        homeId &&
        state.items.some((item) => item.id === homeId)
      ) {
        state.activeId = homeId;
        state.history = [
          homeId,
          ...state.history.filter((id) => id !== homeId),
        ];
      }
    }
  }

  // close other items except the active one
  function closeOthers(targetItemId: string) {
    const idsToClose = state.items
      .filter((item) => item.closable && item.id !== targetItemId)
      .map((item) => item.id);

    if (idsToClose.length === 0) {
      return;
    }

    state.items = state.items.filter((item) => !idsToClose.includes(item.id));
    state.history = state.history.filter((id) => !idsToClose.includes(id));

    state.activeId = targetItemId;
  }

  function closeAll() {
    const idsToClose = state.items
      .filter((item) => item.closable)
      .map((item) => item.id);

    if (idsToClose.length === 0) {
      return;
    }

    state.items = state.items.filter((item) => !idsToClose.includes(item.id));
    state.history = state.history.filter((id) => !idsToClose.includes(id));

    const homeId = resolveHomeId();
    state.activeId = homeId;
  }

  function reorder(nextOrderIds: string[]) {
    state.items = reorderWithAffixFirst(state.items, nextOrderIds);
  }

  function setIndex(itemId: string, desiredIndex: number) {
    const affixIds = state.items
      .filter((item) => item.closable === false)
      .map((item) => item.id);
    const normalIds = state.items
      .filter((item) => item.closable !== false)
      .map((item) => item.id);

    const currentIndex = normalIds.indexOf(itemId);

    if (currentIndex < 0) {
      return;
    }

    const boundedIndex = Math.max(
      0,
      Math.min(desiredIndex, normalIds.length - 1),
    );
    normalIds.splice(boundedIndex, 0, normalIds.splice(currentIndex, 1)[0]);

    reorder([...affixIds, ...normalIds]);
  }

  // initialize the history based on the current activeId
  async function initializeHistory() {
    const lastActiveId = state.activeId;
    const homeId = resolveHomeId();
    const targetId = lastActiveId || homeId;

    const currentId =
      router.currentRoute.value.fullPath || router.currentRoute.value.path;

    if (targetId && targetId !== currentId) {
      const resolved = router.resolve(targetId);

      if ((resolved.matched?.length ?? 0) === 0) {
        if (lastActiveId) {
          close(lastActiveId);
        }

        if (homeId && currentId !== homeId) {
          await router.replace(homeId).catch(() => {});
        }

        return;
      }
      await router.replace(targetId).catch(() => {});
    }
  }

  //  reset state to initial values
  async function resetState() {
    if (!confirm('Are you sure you want to reset the router state?')) {
      return;
    }

    const homeId = resolveHomeId();
    if (homeId && router.currentRoute.value.fullPath !== homeId) {
      await router.replace(homeId).catch(() => {});
    }

    localStorage.removeItem(LS_ITEMS);
    localStorage.removeItem(LS_ACTIVE);
    localStorage.removeItem(LS_HISTORY);
    localStorage.removeItem(LS_SCROLL);

    state.items = [];
    state.history = [];
    state.activeId = null;

    ensureAffixHome();
    window.dispatchEvent(new CustomEvent('itp:state-reset'));
  }

  //  persist
  watch(
    () => state.items,
    (nextItems) => localStorage.setItem(LS_ITEMS, JSON.stringify(nextItems)),
    { deep: true },
  );
  watch(
    () => state.activeId,
    (nextActiveId) =>
      localStorage.setItem(LS_ACTIVE, JSON.stringify(nextActiveId)),
  );
  watch(
    () => state.history,
    (nextHistory) =>
      localStorage.setItem(LS_HISTORY, JSON.stringify(nextHistory)),
    { deep: true },
  );

  //  init + syncs
  ensureAffixHome();
  router.afterEach((to) => {
    syncFromRoute(to);
  });

  return {
    state,
    activeItem,
    close,
    closeOthers,
    closeAll,
    reorder,
    setIndex,
    ensureAffixHome,
    resolveHomeId,
    initializeHistory,
    resetState,
  };
}

export type RouterItemsController = ReturnType<
  typeof createRouterItemsController
>;

export function provideRouterItemsController(
  controller: RouterItemsController,
) {
  provide(ControllerKey, controller);
}

export function useRouterItemsController() {
  const controller = inject<RouterItemsController>(ControllerKey);
  if (!controller) {
    throw new Error('RouterItemsController not provided.');
  }
  return controller;
}
